import React, { useState } from 'react';

import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { Alert, Button, Input } from './common';

interface LoginSignupFormProps {
	isLogin: boolean;
	email: string;
	setEmail: (email: string) => void;
	password: string;
	setPassword: (password: string) => void;
	confirmPassword: string;
	setConfirmPassword: (password: string) => void;
	name: string;
	setName: (name: string) => void;
	loading: boolean;
	error: string;
	onSubmit: (e: React.FormEvent) => void;
	onSwitchMode: (mode: 'login' | 'signup') => void;
	onForgotPassword: () => void;
	onGoogleLogin: () => void;
	isGoogleLoading: boolean;
}

const LoginSignupForm: React.FC<LoginSignupFormProps> = ({
	isLogin,
	email,
	setEmail,
	password,
	setPassword,
	confirmPassword,
	setConfirmPassword,
	name,
	setName,
	loading,
	error,
	onSubmit,
	onSwitchMode,
	onForgotPassword,
	onGoogleLogin,
	isGoogleLoading,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	return (
		<form className="space-y-6" onSubmit={onSubmit}>
			{!isLogin && (
				<Input
					label="Full Name"
					name="name"
					type="text"
					autoComplete="name"
					required={!isLogin}
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="Enter your full name"
				/>
			)}

			<Input
				label="Email address"
				name="email"
				type="email"
				autoComplete="email"
				required
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				placeholder="Enter your email"
			/>

			<div>
				<label
					htmlFor="password"
					className="block text-sm font-medium text-neutral-700 font-primary"
				>
					Password
				</label>
				<div className="mt-1 relative">
					<input
						id="password"
						name="password"
						type={showPassword ? 'text' : 'password'}
						autoComplete={isLogin ? 'current-password' : 'new-password'}
						required
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						className="appearance-none block w-full px-3 py-2 pr-10 border border-neutral-300 rounded-md placeholder-sa-neutral-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm font-primary"
						placeholder="Enter your password"
					/>
					<button
						type="button"
						onClick={() => setShowPassword((v) => !v)}
						className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-500 hover:text-neutral-700 transition-colors"
						aria-label={showPassword ? 'Hide password' : 'Show password'}
					>
						{showPassword ? <EyeIcon size={20} /> : <EyeOffIcon size={20} />}
					</button>
				</div>
			</div>

			{!isLogin && (
				<div>
					<label
						htmlFor="confirmPassword"
						className="block text-sm font-medium text-neutral-700 font-primary"
					>
						Confirm password
					</label>
					<div className="mt-1 relative">
						<input
							id="confirmPassword"
							name="confirmPassword"
							type={showConfirmPassword ? 'text' : 'password'}
							autoComplete="new-password"
							required={!isLogin}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							className="appearance-none block w-full px-3 py-2 pr-10 border border-neutral-300 rounded-md placeholder-sa-neutral-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm font-primary"
							placeholder="Re-enter your password"
						/>
						<button
							type="button"
							onClick={() => setShowConfirmPassword((v) => !v)}
							className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-500 hover:text-neutral-700 transition-colors"
							aria-label={
								showConfirmPassword ? 'Hide password' : 'Show password'
							}
						>
							{showConfirmPassword ? (
								<EyeIcon size={20} />
							) : (
								<EyeOffIcon size={20} />
							)}
						</button>
					</div>
				</div>
			)}

			{error && <Alert variant="error">{error}</Alert>}

			<div className="flex items-center justify-between">
				<Button
					type="submit"
					variant="primary"
					disabled={loading}
					loading={loading}
					fullWidth
				>
					{isLogin ? 'Sign in' : 'Create account'}
				</Button>
			</div>

			{/* Google Sign-in for both login and signup */}
			<div className="relative">
				<div className="absolute inset-0 flex items-center">
					<div className="w-full border-t border-neutral-300" />
				</div>
				<div className="relative flex justify-center text-sm font-primary">
					<span className="px-2 bg-white text-neutral-500">
						Or continue with
					</span>
				</div>
			</div>

			<Button
				type="button"
				variant="outline"
				onClick={onGoogleLogin}
				disabled={isGoogleLoading}
				loading={isGoogleLoading}
				fullWidth
			>
				{!isGoogleLoading && (
					<svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
						<path
							fill="#4285F4"
							d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
						/>
						<path
							fill="#34A853"
							d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
						/>
						<path
							fill="#FBBC05"
							d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
						/>
						<path
							fill="#EA4335"
							d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
						/>
					</svg>
				)}
				{isLogin ? 'Sign in with Google' : 'Sign up with Google'}
			</Button>

			{/* Forgot password only for login */}
			{isLogin && (
				<div className="mt-2 text-right">
					<button
						type="button"
						className="text-sm text-primary-600 hover:text-primary-500 font-primary transition-colors"
						onClick={onForgotPassword}
					>
						Forgot password?
					</button>
				</div>
			)}
		</form>
	);
};

export default LoginSignupForm;
